import { tKey } from 'ember-intl';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import { camelize } from 'frontend-utils/utils/convert-case';
import {
  COLOR_BOOKING,
  COLOR_BOOKING_LY,
  COLOR_TARGET,
  COLOR_TRAFFIC_LIGHT_NEGATIVE,
  COLOR_TRAFFIC_LIGHT_NEUTRAL,
  getCssVariableString,
} from 'frontend-utils/utils/css-variable';

// Max amount of breakdown blocks for Multi Property
export const MAX_AMOUNT_OF_BLOCKS = 6;

// Amount of breakdown blocks per row
export const AMOUNT_OF_BLOCKS_PER_ROW = 3;

// Amount of alowed filters per block in multiproperty
export const MAX_AMOUNT_OF_FILTERS_PER_BLOCK = 7;

// Amount of days in a leap year
export const CAPACITY_MAX_DAYS = 366;

export const MAX_DRAGGABLE_DAYS = 7;

export const WEEKS_IN_YEAR = 52;

// Selectable pace leadtimes (days); default is the first.
export const PACE_LEADTIME_OPTIONS = [7, 14, 30, 90, 180, 365] as const;

export const DEFAULT_PACE_LEADTIME = PACE_LEADTIME_OPTIONS[0];

// Leadtimes above this scan enough reservation data to be cost-relevant. See PIN-1201.
export const PACE_COST_RELEVANT_LEADTIME_DAYS = 90;

export type PaceLeadtime = (typeof PACE_LEADTIME_OPTIONS)[number];

export function isPaceLeadtime(value: number): value is PaceLeadtime {
  return PACE_LEADTIME_OPTIONS.some((option) => option === value);
}

export const SEGMENTATION_BLOCKS_WITHOUT_PACE = [
  RI_BREAKDOWNS.PICKUP,
  RI_BREAKDOWNS.SUBSCRIPTION_ID,
];

export const NORMALIZED_PERIOD = {
  month: 'month',
  week: 'isoWeek',
} as const;

// Revenue insight related consts
// Should be uneven number (equal amount of top/bottom values + potential Other row)
export const NB_OF_SEGMENTATION_VALUES_TO_DISPLAY = 5;

export const INVALID_RESERVATIONS_MESSAGE = 'reservations.invalid_data';

export const RI_SECTIONS = {
  KPI: 'kpi',
  STAY_DATE: 'stay_date',
  PICKUP: 'pickup',
  SEGMENTATION: 'segmentation',
} as const;

export type RiSection = (typeof RI_SECTIONS)[keyof typeof RI_SECTIONS];

export const FILTERABLE_SEGMENTATION_BLOCKS = [
  RI_BREAKDOWNS.BOOKING_LEAD_TIME,
  RI_BREAKDOWNS.COMPANY,
  RI_BREAKDOWNS.GUEST_COUNTRY,
  RI_BREAKDOWNS.IMPACT_DATE,
  RI_BREAKDOWNS.LOS,
  RI_BREAKDOWNS.SEGMENT,
  RI_BREAKDOWNS.ORIGIN,
  RI_BREAKDOWNS.PACE,
  RI_BREAKDOWNS.PICKUP,
  RI_BREAKDOWNS.RATE_CODE,
  RI_BREAKDOWNS.ROOM_TYPE,
  RI_BREAKDOWNS.SOURCE,
  RI_BREAKDOWNS.SOURCE_PROFILE,
  RI_BREAKDOWNS.SUBSCRIPTION_ID,
  RI_BREAKDOWNS.TRAVEL_AGENT,
  RI_BREAKDOWNS.WEEKDAY,
  RI_BREAKDOWNS.CANCELLATION_LEAD_TIME,
] as const;
export type FilterableSegmentationBlock =
  (typeof FILTERABLE_SEGMENTATION_BLOCKS)[number];

export const ALL_SELECTABLE_SEGMENTATION_BLOCKS = [
  RI_BREAKDOWNS.BOOKING_LEAD_TIME,
  RI_BREAKDOWNS.COMPANY,
  RI_BREAKDOWNS.GUEST_COUNTRY,
  RI_BREAKDOWNS.LOS,
  RI_BREAKDOWNS.SEGMENT,
  RI_BREAKDOWNS.ORIGIN,
  RI_BREAKDOWNS.RATE_CODE,
  RI_BREAKDOWNS.ROOM_TYPE,
  RI_BREAKDOWNS.SOURCE_PROFILE,
  RI_BREAKDOWNS.TRAVEL_AGENT,
  RI_BREAKDOWNS.WEEKDAY,
  RI_BREAKDOWNS.CANCELLATION_LEAD_TIME,
];

export const CANCELLATION_ONLY_BLOCKS = [RI_BREAKDOWNS.CANCELLATION_LEAD_TIME];

export const TARGET_BLOCKS = [
  RI_BREAKDOWNS.LOS,
  RI_BREAKDOWNS.SEGMENT,
  RI_BREAKDOWNS.ORIGIN,
  RI_BREAKDOWNS.ROOM_TYPE,
  RI_BREAKDOWNS.SOURCE,
  RI_BREAKDOWNS.WEEKDAY,
];

export const MAPPING_BLOCKS = [
  RI_BREAKDOWNS.RATE_CODE,
  RI_BREAKDOWNS.TRAVEL_AGENT,
  RI_BREAKDOWNS.COMPANY,
  RI_BREAKDOWNS.ROOM_TYPE,
  RI_BREAKDOWNS.WEEKDAY,
  RI_BREAKDOWNS.SOURCE,
  RI_BREAKDOWNS.ORIGIN,
  RI_BREAKDOWNS.GUEST_COUNTRY,
];

// The order is important, some properties are dependant on others
// Please don't change it
export const CALCULATED_PROPERTIES = [
  'BOB',
  'change',
  'relativeChange',
  'reference',
  'budget',
  'budgetChange',
  'forecast',
  'forecastChange',
  'forecastReference',
  'forecastReferenceChange',
  'forecastReferenceRelativeChange',
  'pickupLastYear',
  'cancellationBOB',
  'cancellationBOBOrFiltered',
  'cancellationReference',
  'cancellationReferenceOrFiltered',
  'cancellationChange',
  'cancellationOrFilteredChangePercentage',
  'cancellationOrFilteredChange',
  'cancellationBOBPercentage',
  'cancellationBOBOrFilteredPercentage',
  'cancellationReferencePercentage',
  'cancellationChangePercentage',
  'cancellationReferenceOrFilteredPercentage',
  'grossBOB',
  'grossReference',
  'valueDiffReservations',
  'valueDiffReservationsPercentage',
  'referenceValueDiffReservations',
  'referenceValueDiffReservationsPercentage',
] as const;

export type CalculatedProperty = (typeof CALCULATED_PROPERTIES)[number];

export const CALCULATED_PROPERTIES_NO_PERCENTAGE = [
  'BOB',
  'change',
  'relativeChange',
  'reference',
  'budget',
  'budgetChange',
  'forecast',
  'forecastChange',
  'forecastReference',
  'forecastReferenceChange',
  'forecastReferenceRelativeChange',
  'pickupLastYear',
  'cancellationBOB',
  'cancellationBOBOrFiltered',
  'cancellationReference',
  'cancellationReferenceOrFiltered',
  'cancellationChange',
  'cancellationOrFilteredChange',
  'grossBOB',
  'grossReference',
  'valueDiffReservations',
  'valueDiffReservationsPercentage',
  'referenceValueDiffReservations',
  'referenceValueDiffReservationsPercentage',
];

type CalculatedValueSegmentationDataLike = {
  adr?: number | null;
  adrGroup?: number | null;
  adrTotal?: number | null;
  adrTransient?: number | null;
  capacity?: number | null;
  nrOfRooms?: number | null;
  nrOfRoomsGroup?: number | null;
  nrOfRoomsTotal?: number | null;
  nrOfRoomsTransient?: number | null;
  occupancy?: number | null;
  occupancyGroup?: number | null;
  occupancyTotal?: number | null;
  occupancyTransient?: number | null;
  revenue?: number | null;
  revenueGroup?: number | null;
  revenueTotal?: number | null;
  revenueTransient?: number | null;
};

export const EMPTY_SEGMENTATION_VALUE: CalculatedValueSegmentationDataLike = {
  adr: null,
  nrOfRooms: 0,
  revenue: 0,
  occupancy: 0,
  capacity: 0,
};

const EMPTY_SEGMENTATION_DATA_WITH_KPI_CATEGORY: CalculatedValueSegmentationDataLike =
  {
    adrGroup: null,
    adrTotal: null,
    adrTransient: null,
    nrOfRoomsGroup: 0,
    nrOfRoomsTotal: 0,
    nrOfRoomsTransient: 0,
    revenueGroup: 0,
    revenueTotal: 0,
    revenueTransient: 0,
    occupancyGroup: 0,
    occupancyTotal: 0,
    occupancyTransient: 0,
    capacity: 0,
  };

const NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY: CalculatedValueSegmentationDataLike =
  {
    adrGroup: null,
    adrTotal: null,
    adrTransient: null,
    nrOfRoomsGroup: null,
    nrOfRoomsTotal: null,
    nrOfRoomsTransient: null,
    revenueGroup: null,
    revenueTotal: null,
    revenueTransient: null,
    occupancyGroup: null,
    occupancyTotal: null,
    occupancyTransient: null,
    capacity: null,
  };

export const DUMMY_CALCULATED_VALUES = {
  BOB: { ...EMPTY_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  change: { ...EMPTY_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  reference: { ...EMPTY_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  budget: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  budgetChange: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  forecast: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  forecastChange: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  forecastReference: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  pickupLastYear: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  cancellationBOB: { ...EMPTY_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  cancellationBOBOrFiltered: { ...EMPTY_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  cancellationReference: { ...EMPTY_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  cancellationReferenceOrFiltered: {
    ...EMPTY_SEGMENTATION_DATA_WITH_KPI_CATEGORY,
  },
  cancellationChange: { ...EMPTY_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  cancellationOrFilteredChange: {
    ...EMPTY_SEGMENTATION_DATA_WITH_KPI_CATEGORY,
  },
  cancellationBOBPercentage: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  cancellationBOBOrFilteredPercentage: {
    ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY,
  },
  cancellationReferencePercentage: {
    ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY,
  },
  cancellationReferenceOrFilteredPercentage: {
    ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY,
  },
  cancellationChangePercentage: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  cancellationOrFilteredChangePercentage: {
    ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY,
  },
  grossBOB: { ...EMPTY_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  grossReference: { ...EMPTY_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
};

export const DUMMY_CALCULATED_VALUES_NO_CHANGE = {
  BOB: { ...EMPTY_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  change: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  reference: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  budget: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  budgetChange: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  forecast: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  forecastChange: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  forecastReference: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  pickupLastYear: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  cancellationBOB: { ...EMPTY_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  cancellationBOBOrFiltered: { ...EMPTY_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  cancellationReference: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  cancellationReferenceOrFiltered: {
    ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY,
  },
  cancellationChange: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  cancellationOrFilteredChange: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  cancellationBOBPercentage: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  cancellationBOBOrFilteredPercentage: {
    ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY,
  },
  cancellationReferencePercentage: {
    ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY,
  },
  cancellationReferenceOrFilteredPercentage: {
    ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY,
  },
  cancellationChangePercentage: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  cancellationOrFilteredChangePercentage: {
    ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY,
  },
  grossBOB: { ...EMPTY_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
  grossReference: { ...NULL_SEGMENTATION_DATA_WITH_KPI_CATEGORY },
};

export const KPIS = {
  NR_OF_ROOMS: 'nrOfRooms',
  ADR: 'adr',
  REVENUE: 'revenue',
  OCCUPANCY: 'occupancy',
  REVPAR: 'revpar',
  CAPACITY: 'capacity',
} as const;

export type Kpi = (typeof KPIS)[keyof typeof KPIS];

export const VALID_KPIS = [
  KPIS.OCCUPANCY,
  KPIS.NR_OF_ROOMS,
  KPIS.ADR,
  KPIS.REVENUE,
  KPIS.REVPAR,
];
export type ValidKpi = (typeof VALID_KPIS)[number];

export const ROOMS_TABS = [KPIS.OCCUPANCY, KPIS.NR_OF_ROOMS] as const;
export type RoomsTab = (typeof ROOMS_TABS)[number];

export const REVENUE_TABS = [KPIS.REVENUE, KPIS.REVPAR] as const;
export type RevenueTab = (typeof REVENUE_TABS)[number];

export const KPI_CATEGORIES = {
  TOTAL: 'total',
  TRANSIENT: 'transient',
  GROUP: 'group',
  CONTRACTED_CREW: 'contracted_crew',
  OTHER: 'other',
  UNMAPPED: 'unmapped',
  BLANK: 'blank',
  UNSUPPORTED: 'unsupported',
  OUT_OF_ORDER: 'out_of_order',
} as const;

export type KpiCategory = (typeof KPI_CATEGORIES)[keyof typeof KPI_CATEGORIES];

export const VIEWS = {
  NET: 'net',
  CANCELLATIONS: 'cancellations',
} as const;

export type View = (typeof VIEWS)[keyof typeof VIEWS];

export const CANCELLATIONS_VIEWS = {
  ABSOLUTE: 'absolute',
  PERCENTAGE: 'percentage',
} as const;

export type CancellationsView =
  (typeof CANCELLATIONS_VIEWS)[keyof typeof CANCELLATIONS_VIEWS];

export const REFERENCE_OPTIONS = {
  BUDGET: 'budget',
  FORECAST: 'forecast',
} as const;

export type ReferenceOption =
  (typeof REFERENCE_OPTIONS)[keyof typeof REFERENCE_OPTIONS];

export const SUMMABLE_SHORT_KEYS = [
  KPIS.NR_OF_ROOMS,
  KPIS.REVENUE,
  KPIS.OCCUPANCY,
  KPIS.REVPAR,
  KPIS.CAPACITY,
];

// Combines all of the summable KPIs with the summable KPI categories.
const SUMMABLE_LONG_KEYS = [
  KPIS.NR_OF_ROOMS,
  KPIS.REVENUE,
  KPIS.OCCUPANCY,
  KPIS.REVPAR,
]
  .map((kpi) =>
    [
      KPI_CATEGORIES.TOTAL,
      KPI_CATEGORIES.TRANSIENT,
      KPI_CATEGORIES.GROUP,
      KPI_CATEGORIES.CONTRACTED_CREW,
      KPI_CATEGORIES.OTHER,
      KPI_CATEGORIES.UNMAPPED,
    ].map((kpiCategory) => camelize(`${kpi}_${kpiCategory}`)),
  )
  .flat();

export const SUMMABLE_METRIC_KEYS = [
  ...SUMMABLE_SHORT_KEYS,
  ...SUMMABLE_LONG_KEYS,
];

export const DUMMY_SEGMENTATION_VALUE = {
  adr: null,
  nrOfRooms: null,
  revenue: null,
  occupancy: null,
  adrGroup: null,
  adrTotal: null,
  adrTransient: null,
  nrOfRoomsGroup: null,
  nrOfRoomsTotal: null,
  nrOfRoomsTransient: null,
  revenueGroup: null,
  revenueTotal: null,
  revenueTransient: null,
  occupancyGroup: null,
  occupancyTotal: null,
  occupancyTransient: null,
  capacity: null,
};

export const DEFAULT_SEGMENTATION_BLOCKS_ORDER = [
  // Segmentation
  {
    segmentationKey: RI_BREAKDOWNS.SEGMENT,
    translationKey: tKey(`revenue_insight.${RI_BREAKDOWNS.SEGMENT}`),
    isVisible: true,
    isMappingVisible: false,
  },
  // Rate code
  {
    segmentationKey: RI_BREAKDOWNS.RATE_CODE,
    translationKey: tKey(`revenue_insight.${RI_BREAKDOWNS.RATE_CODE}`),
    isVisible: true,
    isMappingVisible: false,
  },
  // Travel agent
  {
    segmentationKey: RI_BREAKDOWNS.TRAVEL_AGENT,
    translationKey: tKey(`revenue_insight.${RI_BREAKDOWNS.TRAVEL_AGENT}`),
    isVisible: true,
    isMappingVisible: false,
  },
  // Company
  {
    segmentationKey: RI_BREAKDOWNS.COMPANY,
    translationKey: tKey(`revenue_insight.${RI_BREAKDOWNS.COMPANY}`),
    isVisible: true,
    isMappingVisible: false,
  },
  // Room type
  {
    segmentationKey: RI_BREAKDOWNS.ROOM_TYPE,
    translationKey: tKey(`revenue_insight.${RI_BREAKDOWNS.ROOM_TYPE}`),
    isVisible: true,
    isMappingVisible: false,
  },
  // Booking window
  {
    segmentationKey: RI_BREAKDOWNS.BOOKING_LEAD_TIME,
    translationKey: tKey(`revenue_insight.${RI_BREAKDOWNS.BOOKING_LEAD_TIME}`),
    isVisible: true,
    isMappingVisible: false,
  },
  // Length of stay
  {
    segmentationKey: RI_BREAKDOWNS.LOS,
    translationKey: tKey(`revenue_insight.${RI_BREAKDOWNS.LOS}`),
    isVisible: true,
    isMappingVisible: false,
  },
  // Day of week
  {
    segmentationKey: RI_BREAKDOWNS.WEEKDAY,
    translationKey: tKey(`revenue_insight.${RI_BREAKDOWNS.WEEKDAY}`),
    isVisible: true,
    isMappingVisible: false,
  },
  // Guest country
  {
    segmentationKey: RI_BREAKDOWNS.GUEST_COUNTRY,
    translationKey: tKey(`revenue_insight.${RI_BREAKDOWNS.GUEST_COUNTRY}`),
    isVisible: true,
    isMappingVisible: false,
  },
  // Origin
  {
    segmentationKey: RI_BREAKDOWNS.ORIGIN,
    translationKey: tKey(`revenue_insight.${RI_BREAKDOWNS.ORIGIN}`),
    isVisible: false,
    isMappingVisible: false,
  },
  // Source
  {
    segmentationKey: RI_BREAKDOWNS.SOURCE,
    translationKey: tKey(`revenue_insight.${RI_BREAKDOWNS.SOURCE}`),
    isVisible: false,
    isMappingVisible: false,
  },
  // Source profile
  {
    segmentationKey: RI_BREAKDOWNS.SOURCE_PROFILE,
    translationKey: tKey(`revenue_insight.${RI_BREAKDOWNS.SOURCE_PROFILE}`),
    isVisible: false,
    isMappingVisible: false,
  },
  // Cancellation lead time
  {
    segmentationKey: RI_BREAKDOWNS.CANCELLATION_LEAD_TIME,
    translationKey: tKey(
      `revenue_insight.${RI_BREAKDOWNS.CANCELLATION_LEAD_TIME}`,
    ),
    isVisible: false,
    isMappingVisible: false,
  },
];

// Hotel breakdown can be sorted by either kpi or change column
export const HOTEL_SORT_BY = {
  KPI: 'kpi', // Refers to room nights, ADR, Revenues and Occupancy
  CHANGE: 'change', // Refers to "vs. Last year", "vs. Budget", etc.
} as const;

export type HotelSortBy = (typeof HOTEL_SORT_BY)[keyof typeof HOTEL_SORT_BY];

export const CHART_CANCELLATIONS_PATTERN = {
  pattern: {
    path: {
      d: 'M 0 -1 L 18 17 M 3 -1 L 18 14 M -1 1 L 18 20 M -1 4 L 18 23 M 6 -1 L 18 11 M 9 -1 L 18 8 M -1 7 L 18 26 M -1 10 L 18 29 M -1 13 L 18 32 M 12 -1 L 18 5 M 15 -1 L 19 3 M 15 32 L -1 16 M 12 32 L -1 19 M 9 32 L -1 22 M 6 32 L -1 25 M 3 32 L -1 28',
      strokeWidth: 1,
      stroke: getCssVariableString(COLOR_TRAFFIC_LIGHT_NEGATIVE),
    },
    width: 18,
    height: 18,
  },
};

export const PROGRESS_BAR_COLOUR_MAPPING = {
  BOB: getCssVariableString(COLOR_BOOKING),
  reference: getCssVariableString(COLOR_BOOKING_LY),
  budget: getCssVariableString(COLOR_TARGET),
  forecast: getCssVariableString(COLOR_TARGET),
  pickupLastYear: getCssVariableString(COLOR_BOOKING_LY),
  cancellationBOB: getCssVariableString(COLOR_TRAFFIC_LIGHT_NEGATIVE),
  cancellationBOBOrFiltered: getCssVariableString(COLOR_TRAFFIC_LIGHT_NEGATIVE),
  cancellationReference: getCssVariableString(COLOR_TRAFFIC_LIGHT_NEGATIVE),
  cancellationReferenceOrFiltered: getCssVariableString(
    COLOR_TRAFFIC_LIGHT_NEGATIVE,
  ),
  positive: 'positive',
  negative: 'negative',
};

export const REVENUE_INSIGHT_METRICS = {
  BOB: 'BOB',
  REFERENCE: 'reference',
  CHANGE: 'change',
  RELATIVE_CHANGE: 'relativeChange',
  CANCELLATION_BOB: 'cancellationBOB',
  CANCELLATION_BOB_OR_FILTERED: 'cancellationBOBOrFiltered',
  CANCELLATION_REFERENCE: 'cancellationReference',
  CANCELLATION_REFERENCE_OR_FILTERED: 'cancellationReferenceOrFiltered',
  FORECAST_REFERENCE: 'forecastReference',
  FORECAST_REFERENCE_CHANGE: 'forecastReferenceChange',
  FORECAST_REFERENCE_RELATIVE_CHANGE: 'forecastReferenceRelativeChange',
  PICKUP_LAST_YEAR: 'pickupLastYear',
  GROSS_BOB: 'grossBOB',
  GROSS_REFERENCE: 'grossReference',
  POSITIVE: 'positive',
  NEGATIVE: 'negative',
} as const;

export const REVENUE_INSIGHT_SUBTITLE_TRANSLATION_KEYS = {
  [REVENUE_INSIGHT_METRICS.BOB]: tKey('revenue_insight.metrics.subtitle.BOB'),
  [REVENUE_INSIGHT_METRICS.REFERENCE]: tKey(
    'revenue_insight.metrics.subtitle.reference',
  ),
  [REVENUE_INSIGHT_METRICS.PICKUP_LAST_YEAR]: tKey(
    'revenue_insight.metrics.subtitle.pickupLastYear',
  ),
  [REVENUE_INSIGHT_METRICS.CANCELLATION_BOB]: tKey(
    'revenue_insight.metrics.subtitle.cancellationBOB',
  ),
  [REVENUE_INSIGHT_METRICS.CANCELLATION_REFERENCE]: tKey(
    'revenue_insight.metrics.subtitle.cancellationReference',
  ),
  [REVENUE_INSIGHT_METRICS.GROSS_BOB]: tKey(
    'revenue_insight.metrics.subtitle.grossBOB',
  ),
  [REVENUE_INSIGHT_METRICS.GROSS_REFERENCE]: tKey(
    'revenue_insight.metrics.subtitle.grossReference',
  ),
  [REVENUE_INSIGHT_METRICS.CANCELLATION_BOB_OR_FILTERED]: tKey(
    'revenue_insight.metrics.subtitle.cancellationBOB',
  ),
  [REVENUE_INSIGHT_METRICS.CANCELLATION_REFERENCE_OR_FILTERED]: tKey(
    'revenue_insight.metrics.subtitle.cancellationReference',
  ),
};

export const COLOR_VALUE_MAPPING = {
  [getCssVariableString(COLOR_TRAFFIC_LIGHT_NEUTRAL)]: 'text-general-info-600',
  [getCssVariableString(COLOR_BOOKING)]: 'text-info-emphasis',
  [getCssVariableString(COLOR_BOOKING_LY)]: 'text-info-default',
  [getCssVariableString(COLOR_TARGET)]: 'text-general-neutral-900',
  [getCssVariableString(COLOR_TRAFFIC_LIGHT_NEGATIVE)]: 'error-text',
  positive: 'text-success',
  negative: 'text-critical',
};

export const VALUE_CLASS_MAPPING = {
  BOB: COLOR_VALUE_MAPPING[PROGRESS_BAR_COLOUR_MAPPING.BOB],
  reference: COLOR_VALUE_MAPPING[PROGRESS_BAR_COLOUR_MAPPING.reference],
  grossBOB: COLOR_VALUE_MAPPING[PROGRESS_BAR_COLOUR_MAPPING.BOB],
  grossReference: COLOR_VALUE_MAPPING[PROGRESS_BAR_COLOUR_MAPPING.reference],
  budget: COLOR_VALUE_MAPPING[PROGRESS_BAR_COLOUR_MAPPING.budget],
  forecast: COLOR_VALUE_MAPPING[PROGRESS_BAR_COLOUR_MAPPING.forecast],
  pickupLastYear:
    COLOR_VALUE_MAPPING[PROGRESS_BAR_COLOUR_MAPPING.pickupLastYear],
  cancellationBOB:
    COLOR_VALUE_MAPPING[PROGRESS_BAR_COLOUR_MAPPING.cancellationBOB],
  cancellationBOBOrFiltered:
    COLOR_VALUE_MAPPING[PROGRESS_BAR_COLOUR_MAPPING.cancellationBOB],
  cancellationReference:
    COLOR_VALUE_MAPPING[PROGRESS_BAR_COLOUR_MAPPING.cancellationReference],
  cancellationReferenceOrFiltered:
    COLOR_VALUE_MAPPING[PROGRESS_BAR_COLOUR_MAPPING.cancellationReference],
  positive: COLOR_VALUE_MAPPING[PROGRESS_BAR_COLOUR_MAPPING.positive],
  negative: COLOR_VALUE_MAPPING[PROGRESS_BAR_COLOUR_MAPPING.negative],
};
